import { Formik, useFormikContext } from "formik";
import { useEffect } from "react";
import apiRequest from "../../../utils/admin/apiRequest";
import InputField from "../../ui/form/InputField";
import Button from "../../ui/utils/Button";
import SelectField from "../../ui/form/SelectField";
import usePost from "../../../hooks/api/usePost";
import Alert from "../../ui/utils/Alert";
import useFetch from "../../../hooks/api/useFetch";
import { updateCorporateCodeValidationSchema } from "../../../validation/admin/corporateCodeValidation";

const LocationWatcher = ({
  fetchStates,
  fetchCities,
  setBlankResponse,
  setCityBlankResponse,
}) => {
  const { values, setFieldValue } = useFormikContext();

  useEffect(() => {
    if (values.countryId) {
      fetchStates({ countryId: values.countryId });

      if (!values.stateId) {
        setFieldValue("stateId", "");
      }
    } else {
      setBlankResponse();
    }
  }, [values.countryId]);

  useEffect(() => {
    if (values.stateId) {
      fetchCities({ stateId: values.stateId });

      if (!values.cityId) {
        setFieldValue("cityId", "");
      }
    } else {
      setCityBlankResponse();
    }
  }, [values.stateId]);

  return null;
};

/* ---------- Main Component ---------- */
const EditCorporateCode = ({ data, closeModal, callback }) => {
  const { error, isLoading, post, serverErrors } = usePost(
    apiRequest.editCorporateCode
  );

  const initialValues = {
    corporateName: data?.corporateName || "",
    corporateCode: data?.corporateCode || "",
    buildingName: data?.buildingName || "",
    pincode: data?.pincode || "",
    countryId: data?.countryId?._id || "",
    stateId: data?.stateId?._id || "",
    cityId: data?.cityId?._id || "",
  };

  const { data: countryData } = useFetch({
    apiMethod: apiRequest.getCountry,
    body: { paginate: false, sortField: "name", sortOrder: "asc" },
  });

  const { data: stateData, refetch: fetchStates, setBlankResponse } = useFetch({
    apiMethod: apiRequest.getStateByCountryId,
    autoFetch: false,
  });

  const {
    data: cityData,
    refetch: fetchCities,
    setBlankResponse: setCityBlankResponse,
  } = useFetch({
    apiMethod: apiRequest.getCityByState,
    autoFetch: false,
  });



  return (
    <div>
      <h2 className="p-4 text-lg font-semibold">Update Corporate Code</h2>
      <div className="border-b" />

      <Formik
        initialValues={initialValues}
        validationSchema={updateCorporateCodeValidationSchema}
        validateOnBlur
        validateOnChange
        onSubmit={async (values, { resetForm }) => {
          const response = await post({
            data: { body: values, id: data?._id },
          });

          if (response?.response) {
            callback?.();
            resetForm();
            closeModal();
          }
        }}
      >
        {({ values, handleChange, handleBlur, handleSubmit, touched, errors }) => (
          <>
            <LocationWatcher
              data={data}
              fetchStates={fetchStates}
              fetchCities={fetchCities}
              setBlankResponse={setBlankResponse}
              setCityBlankResponse={setCityBlankResponse}
            />

            <form onSubmit={handleSubmit} className="text-sm max-h-[80vh] overflow-auto">
              {error && <Alert message={error} type="error" />}

              <div className="grid grid-cols-1 gap-2 p-4 sm:grid-cols-2">
                <InputField
                  name="corporateName"
                  label="Corporate Name"
                  value={values.corporateName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.corporateName && errors.corporateName}
                  serverError={serverErrors?.corporateName}
                />
                <InputField
                  name="buildingName"
                  label="Building Name"
                  value={values.buildingName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.buildingName && errors.buildingName}
                  serverError={serverErrors?.buildingName}
                />

                <InputField
                  name="corporateCode"
                  label="Corporate Code"
                  value={values.corporateCode}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.corporateCode && errors.corporateCode}
                  serverError={serverErrors?.corporateCode}
                />

                <InputField
                  name="pincode"
                  label="Pincode"
                  value={values.pincode}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.pincode && errors.pincode}
                  serverError={serverErrors?.pincode}
                />

                <SelectField
                  name="countryId"
                  label="Country"
                  value={values.countryId}
                  onChange={handleChange}
                  options={countryData?.countries?.map((c) => ({
                    value: c._id,
                    label: c.name,
                  }))}
                  error={touched.countryId && errors.countryId}
                />

                <SelectField
                  name="stateId"
                  label="State"
                  value={values.stateId}
                  onChange={handleChange}
                  options={stateData?.states?.map((s) => ({
                    value: s._id,
                    label: s.name,
                  }))}
                  error={touched.stateId && errors.stateId}
                />

                <SelectField
                  name="cityId"
                  label="City"
                  value={values.cityId}
                  onChange={handleChange}
                  options={cityData?.cities?.map((c) => ({
                    value: c._id,
                    label: c.name,
                  }))}
                  error={touched.cityId && errors.cityId}
                />

              </div>

              <div className="border-b" />

              <div className="flex justify-end gap-2 p-4">
                <Button variant="secondary" onClick={closeModal}>
                  Cancel
                </Button>
                <Button type="submit" variant="primary" isLoading={isLoading}>
                  Update
                </Button>
              </div>
            </form>
          </>
        )}
      </Formik>
    </div>
  );
};

export default EditCorporateCode;
